import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllSubdomains, deleteSubdomain, getAllServices } from '../services/api.service';
import Loading from '../components/loading';
import { FaTimes } from 'react-icons/fa';
import Helper from '../services/helper.service';
import { useConfirm } from '../components/confirm-dialog';

const SubdomainsListPage = () => {
  const [subdomains, setSubdomains] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filteredSubdomains, setFilteredSubdomains] = useState([]);
  const navigate = useNavigate();
  const confirm = useConfirm();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [subs, servs] = await Promise.all([
          getAllSubdomains(),
          getAllServices()
        ]);
        setSubdomains(subs);
        setFilteredSubdomains(subs);
        setServices(servs);

      } catch (err) {
        Helper.toast("Erreur lors du chargement des données", "error");

      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const lower = search.toLowerCase();
    setFilteredSubdomains(
      subdomains.filter(s =>
        s.fullName?.toLowerCase().includes(lower) ||
        s.subdomain?.toLowerCase().includes(lower)
      )
    );
  }, [search, subdomains]);

  const handleEdit = (subdomain) => {
    navigate(`/subdomain/${subdomain}`);
  };

  const handleAddNew = () => {
    navigate(`/subdomain/new`);
  };

  const handleDelete = async (sub) => {
    if (!sub?._id) return;

    const ok = await confirm({
      title: "Attention !",
      message: <>Supprimer le sous-domaine <strong>{sub.fullName}</strong> ?</>,
      confirmText: "Oui",
      cancelText: "Non",
      tone: "warning",
    });
    if (!ok) return Helper.toast("Action annulée", "info");

    try {
      await deleteSubdomain(sub.subdomain, { firstName: 'Admin', lastName: 'Système' });
      setSubdomains(prev => prev.filter(s => s._id !== sub._id)); // ✅ compare ids
    } catch {
      Helper.toast("Erreur lors de la suppression", "error");
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gestion des sous-domaines</h1>
      </div>

      <div className='flex flex-col md:flex-row justify-between items-start md:items-center mb-6'>
        <div className="relative w-full max-w-md">
          <input
            type="text"
            placeholder="Rechercher un sous-domaine"
            className="w-full border border-gray-300 p-2 pr-10 rounded"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-3 text-gray-500 text-sm"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          )}
        </div>

        <button
          onClick={handleAddNew}
          className="bg-primary hover:bg-blue-700 text-white px-4 py-2 rounded shadow"
        >
          Ajouter un sous-domaine
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loading />
        </div>
      ) : filteredSubdomains.length ? (
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {filteredSubdomains.map((sub) => (
            <div
              key={sub.subdomain}
              className="p-4 rounded-lg shadow-sm border hover:shadow-md transition bg-white flex flex-col justify-between"
            >
              <div>
                {sub.logoUrl && (
                  <img
                    src={sub.logoUrl}
                    alt={`Logo ${sub.fullName}`}
                    className="h-12 object-contain mb-4"
                  />
                )}
                <h2 className="text-lg font-semibold text-gray-800 mb-1">{sub.fullName}</h2>

                <p className="text-sm text-gray-600">
                  URL :{" "}
                  <a
                    href={`https://${sub.subdomain}.services.fiskagroup.com`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline"
                  >
                    {`https://${sub.subdomain}.services.fiskagroup.com`}
                  </a>
                </p>

                {sub.services?.length > 0 ? (
                  <div className="mt-3 space-y-1">
                    <p className="text-sm font-semibold text-gray-700">Prestations associées :</p>
                    <ul className="text-sm text-gray-600 list-disc list-inside">
                      {sub.services.map((service, idx) => {
                        const matchedService = services.find(s => s._id === service.serviceId);
                        const title = matchedService?.title?.fr || matchedService?.title?.en || 'Service inconnu';

                        return (
                          <li key={idx}>
                            {title} – {service.customPrice?.toFixed(2) || '0.00'} €
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-gray-500 italic">
                    Aucune prestation associée à ce sous-domaine.
                  </p>
                )}
              </div>

              <div className="mt-4 space-y-2">
                <button
                  onClick={() => handleEdit(sub.subdomain)}
                  className="w-full py-2 px-4 bg-gray-800 text-white rounded hover:bg-gray-700 transition"
                >
                  Modifier
                </button>
                <button
                  onClick={() => handleDelete(sub)}
                  className="w-full py-2 px-4 bg-red-600 text-white rounded hover:bg-red-700 transition"
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-500 mt-12">
          Aucun sous-domaine trouvé. Cliquez sur "Ajouter un sous-domaine" pour commencer.
        </p>
      )}
    </div>
  );
};

export default SubdomainsListPage;